import React, { ReactElement, useRef, useState, useEffect } from "react";
import { SiteReadDto } from "../../../../Models/Site/SiteReadDto";
import { SiteCreateDto } from "../../../../Models/Site/SiteCreateDto";
import useIdentityStore from "../../../../Zustand/identityStore";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../../messages/en-US";
import { Grid, GridCellProps, GridColumn, GridDataStateChangeEvent, GridToolbar } from "@progress/kendo-react-grid";
import { 
  DataResult, 
  SortDescriptor, 
  State, 
  process
} from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Button } from "@progress/kendo-react-buttons";
import { pencilIcon, plusIcon, downloadIcon, clipboardIcon, infoCircleIcon } from "@progress/kendo-svg-icons";
import SiteDialog from "../Dialogs/SiteDialog";
import { GetEndpointIcon, GetHeadUnitIcon, GetBulkFlowIcon } from "../../../../Utilities/CustomIcons";
import { SvgIcon } from "@progress/kendo-react-common";
import { Tooltip } from '@progress/kendo-react-tooltip';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeartPulse } from "@fortawesome/free-solid-svg-icons";

interface Props {
  sites: Array<any>;
  createSite: (createSite: SiteCreateDto) => void;
  updateSite: (siteId: string, updateSite: SiteCreateDto) => void;
  height: number;
}

export default function AdministrationSiteTable(props: Props): ReactElement {
  const currentUserRole = useIdentityStore((x) => x.user?.profile.role);
  const localizationService = useLocalization();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<SiteReadDto | null>(null);

  const initialSort: Array<SortDescriptor> = [
    { field: "name", dir: "asc" },
  ];
  const [sort] = useState(initialSort);

  const _export = useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  // Creating dataState to prevent issues with virtual scrolling
  const createDataState = (dataState: State) => {
    return {
      result: process(props.sites.slice(0), dataState),
      dataState: dataState,
    };
  };

  let initialState = createDataState({
    take: 30,
    skip: 0,
    sort: sort,
  });

  const [result, setResult] = useState<DataResult>(initialState.result);
  const [dataState, setDataState] = useState<State>(initialState.dataState);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    let updatedState = createDataState(event.dataState);
    setResult(updatedState.result);
    setDataState(updatedState.dataState);
  };

  useEffect(() => {
    let updatedState = createDataState(dataState);
    setResult(updatedState.result);
    setDataState(updatedState.dataState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.sites]);

  const getExcelExportData = () => {
    return process(props.sites, {
      filter: dataState.filter,
      sort: dataState.sort,
    }).data;
  };

  const openAddDialog = () => {
    setSelectedSite(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (site: SiteReadDto) => {
    setSelectedSite(site);
    setIsDialogOpen(true);
  }

  const FeaturesHeaderCell = (props: any) => {
    const TooltipContent = () => (
      <div className="p-2">
        <div className="flex items-center gap-2 mb-2">
          {GetHeadUnitIcon(20, 20)}
          <span>Manual System</span>
        </div>
        <div className="flex items-center gap-2 mb-2">
          {GetEndpointIcon(20, 20)}
          <span>Automatic System</span>
        </div>
        <div className="flex items-center gap-2 mb-2">
          {GetBulkFlowIcon(20, 20)}
          <span>Flow Rate System</span>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <SvgIcon icon={clipboardIcon} size="medium" />
          <span>Asset Dashboard</span>
        </div>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faHeartPulse} />
          <span>Health Dashboard</span>
        </div>
      </div>
    );

    return (
      <span onClick={props.onClick} className={'k-cell-inner'}>
        <span className={'k-link'}>
          <span className={'k-column-title'}>Features</span>{' '}
          <Tooltip 
            anchorElement="target" 
            position="bottom"
            parentTitle={true}
            content={TooltipContent}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                marginLeft: '0.75rem',
                width: '26px',
                height: '26px'
              }}
              title=" "
            >
              <SvgIcon icon={infoCircleIcon} />
            </span>
          </Tooltip>
          {props.children}
        </span>       
      </span>
    );
  };

  const EnabledFeaturesCell = (props: GridCellProps) => {
    const { dataItem } = props;
    const enabledFeatures: any[] = [];
    
    if (dataItem.softwareFeatures?.isManualSystemEnabled) {
      enabledFeatures.push(
        <Tooltip key="manual" anchorElement="target" position="top" parentTitle={true} content={() => "Manual System"}>
          <span style={{ display: 'inline-flex', alignItems: 'center' }} title=" ">
            {GetHeadUnitIcon(23, 23)}
          </span>
        </Tooltip>
      );
    }
    if (dataItem.softwareFeatures?.isAutomaticSystemEnabled) {
      enabledFeatures.push(
        <Tooltip key="automatic" anchorElement="target" position="top" parentTitle={true} content={() => "Automatic System"}>
          <span style={{ display: 'inline-flex', alignItems: 'center' }} title=" ">
            {GetEndpointIcon(19, 19)}
          </span>
        </Tooltip>
      );
    }
    if (dataItem.softwareFeatures?.isFlowRateSystemEnabled) {
      enabledFeatures.push(
        <Tooltip key="flow" anchorElement="target" position="top" parentTitle={true} content={() => "Flow Rate System"}>
          <span style={{ display: 'inline-flex', alignItems: 'center' }} title=" ">
            {GetBulkFlowIcon(20, 20)}
          </span>
        </Tooltip>
      );
    }
    if (dataItem.softwareFeatures?.isAssetDashboardEnabled) {
      enabledFeatures.push(
        <Tooltip key="asset" anchorElement="target" position="top" parentTitle={true} content={() => "Asset Dashboard"}>
          <span style={{ display: 'inline-flex', alignItems: 'center' }} title=" ">
            <SvgIcon icon={clipboardIcon} size="large" />
          </span>
        </Tooltip>
      );
    }
    if (dataItem.softwareFeatures?.isHealthDashboardEnabled) {
      enabledFeatures.push(
        <Tooltip key="health" anchorElement="target" position="top" parentTitle={true} content={() => "Health Dashboard"}>
          <span style={{ display: 'inline-flex', alignItems: 'center' }} title=" ">
            <FontAwesomeIcon icon={faHeartPulse} size="lg" />
          </span>
        </Tooltip>
      );
    }
    
    return (
      <td>
        <div className="flex items-center gap-1 flex-wrap">
          {enabledFeatures.length > 0 ? enabledFeatures : "-"}
        </div>
      </td>
    );
  };


  return (
    <div>
      <ExcelExport
        data={getExcelExportData()}
        ref={_export}
        collapsible={true}
        fileName="Sites"
      >
        <ExcelExportColumn field="name" title={localizationService.toLanguageString("custom.name", enMessages.custom.name)} />
        <ExcelExportColumn field="description" title={localizationService.toLanguageString("custom.description", enMessages.custom.description)} />
        <ExcelExportColumn field="timezone" title={localizationService.toLanguageString("custom.timezone", enMessages.custom.timezone)} />
        <ExcelExportColumn field="softwareFeatures.isManualSystemEnabled" title="Manual System" />
        <ExcelExportColumn field="softwareFeatures.isAutomaticSystemEnabled" title="Automatic System" />
        <ExcelExportColumn field="softwareFeatures.isFlowRateSystemEnabled" title="Flow Rate System" />
        <ExcelExportColumn field="softwareFeatures.isAssetDashboardEnabled" title="Asset Dashboard" />
        <ExcelExportColumn field="softwareFeatures.isHealthDashboardEnabled" title="Health Dashboard" />
      </ExcelExport>

      <Grid
        key="SitesTable"
        data={result}
        scrollable="virtual"
        take={30}
        rowHeight={46}
        onDataStateChange={dataStateChange}
        {...dataState}
        sortable={{
          mode: "multiple",
        }}
        filterable={true}
        style={{
          height: props.height,
        }}
      >
        <GridToolbar>
          <div className="w-full flex justify-between items-center">
            <div className="gb-h3">Accounts</div>
            <div className="flex gap-2"> 
              <Button
                title={`${localizationService.toLanguageString('custom.add', enMessages.custom.add)} ${localizationService.toLanguageString('custom.account', enMessages.custom.account)}`}
                onClick={openAddDialog}
                themeColor="primary"
                svgIcon={plusIcon}
              >
                {localizationService.toLanguageString('custom.add', enMessages.custom.add)} {localizationService.toLanguageString('custom.account', enMessages.custom.account)}
              </Button>
              <Button
                title={localizationService.toLanguageString('custom.exportToExcel', enMessages.custom.exportToExcel)}
                onClick={excelExport}
                themeColor="base"
                svgIcon={downloadIcon}
                style={{ marginRight: "10px" }}
              />
            </div>
          </div>
        </GridToolbar>

        <GridColumn 
          field="name" 
          title={localizationService.toLanguageString("custom.name", enMessages.custom.name)} 
        />
        <GridColumn 
          field="description" 
          title={localizationService.toLanguageString("custom.description", enMessages.custom.description)} 
        />
        <GridColumn 
          field="timezone" 
          title={localizationService.toLanguageString("custom.timezone", enMessages.custom.timezone)} 
        />
        <GridColumn 
          field="softwareFeatures.isManualSystemEnabled" 
          title="Features"
          headerCell={FeaturesHeaderCell}
          cell={EnabledFeaturesCell}
        />
        {currentUserRole !== "GeneralUser" && (
          <GridColumn
            title={localizationService.toLanguageString("custom.actions", enMessages.custom.actions)}
            width="100px"
            sortable={false}
            filterable={false}
            cell={(cellProps) => {
              return (
                <td style={{textAlign: 'center'}}>
                  <Button
                    className="mr-1"
                    themeColor="primary"
                    svgIcon={pencilIcon}
                    onClick={() => openEditDialog(cellProps.dataItem)}
                    title={localizationService.toLanguageString("custom.edit", enMessages.custom.edit)}
                  />
                </td>
              );
            }}
          />
        )}
      </Grid>

      {isDialogOpen && (
        <SiteDialog
          site={selectedSite}
          existingSites={props.sites}
          onClose={() => {
            setIsDialogOpen(false);
            setSelectedSite(null);
          }}
          onSave={(siteId, siteData) => {
            if (siteId) {
              props.updateSite(siteId, siteData);
            } else {
              props.createSite(siteData);
            }
            setIsDialogOpen(false);
            setSelectedSite(null);
          }}
        />
      )}
    </div>
  );
}
